import { embeddablePaths } from '@config/paths';
import { isBrowser } from './isBrowser';
import { getPathWithoutLocale } from './websiteUrlUtils';

const MAX_ATTEMPTS = 30;
const RETRY_DELAY_MS = 300;

/** Fallback when Intercom Messenger is not loaded (embeddable paths). */
export const INTERCOM_FALLBACK_MAILTO = 'mailto:support@hyperhuman.cc';

type IntercomFn = (command: string, ...args: unknown[]) => void;

/**
 * Opens a fresh Intercom conversation via `Intercom('showNewMessage')`.
 *
 * The Intercom loader (injected via Helmet in `Head`) stubs and queues calls,
 * but `window.Intercom` may not exist the instant a deep-link handler runs after
 * hydration, so this retries until the snippet is present. Mirrors the retry
 * approach in `scheduleScrollToFeatureRowId` for `?feature=` deep links.
 *
 * No-op when not in a browser or on pages where Intercom is not loaded
 * (embeddable paths) - in that case it simply gives up after MAX_ATTEMPTS.
 *
 * @returns A function to cancel any pending retry (e.g. on unmount)
 */
export function openNewIntercomChat(): () => void {
  if (!isBrowser()) {
    return () => undefined;
  }

  let attempts = 0;
  let timerId: number | null = null;
  let cancelled = false;

  const clearTimer = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const tryOpen = () => {
    if (cancelled) return;
    const intercom = (window as unknown as { Intercom?: IntercomFn }).Intercom;
    if (typeof intercom === 'function') {
      intercom('showNewMessage');
      return;
    }
    attempts += 1;
    if (attempts < MAX_ATTEMPTS) {
      clearTimer();
      timerId = window.setTimeout(tryOpen, RETRY_DELAY_MS);
    }
  };

  tryOpen();

  return () => {
    cancelled = true;
    clearTimer();
  };
}

/**
 * Click handler for CMS CTAs whose `button_link` is the Intercom ask wire
 * format (`?ask=1`). Opens Messenger in-place, or falls back to mailto on
 * embeddable paths where the snippet is not injected.
 */
export function handleIntercomCtaClick(): void {
  if (!isBrowser()) return;
  const isEmbeddable = embeddablePaths.includes(
    getPathWithoutLocale(window.location.pathname),
  );
  if (isEmbeddable) {
    window.location.href = INTERCOM_FALLBACK_MAILTO;
    return;
  }
  openNewIntercomChat();
}

/**
 * Anchor click for `href="?ask=1"`. Keeps the no-reload Messenger open.
 * Without JavaScript the href still reaches the layout deep-link.
 */
export function handleIntercomAnchorClick(event: { preventDefault: () => void }): void {
  event.preventDefault();
  handleIntercomCtaClick();
}
